import { useEffect, useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";
import { useParticipant } from "../../../contexts/ParticipantContext";
import { useHolidaySchedule } from "../../../contexts/HolidayScheduleContext";
import { HolidaySchedule } from "../../../models/HolidaySchedule";
import {
  HolidayAssignmentMode,
  holidayAssignmentModes,
} from "../../../models/HolidayAssignmentMode";
import moment from "moment";
import { HolidayRule } from "../../../models/HolidayRule";
import { Participant } from "../../../models/Participant";

interface Props {
  holidayScheduleId: HolidaySchedule["_id"];
  holidayRule?: HolidayRule;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Errors {
  name?: string;
}

const HolidayRuleDialog: React.FC<Props> = ({
  holidayScheduleId,
  holidayRule,
  isOpen,
  setIsOpen,
}) => {
  const { participants } = useParticipant();
  const { createHolidayRule, updateHolidayRule } = useHolidaySchedule();

  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [notes, setNotes] = useState("");
  const [assignmentMode, setAssignmentMode] =
    useState<HolidayAssignmentMode>("every");
  const [everyYearParticipant, setEveryYearParticipant] =
    useState<Participant["_id"]>();
  const [evenYearsParticipant, setEvenYearsParticipant] =
    useState<Participant["_id"]>();
  const [oddYearsParticipant, setOddYearsParticipant] =
    useState<Participant["_id"]>();
  const [startOffsetMinutes, setStartOffsetMinutes] = useState(0);
  const [endOffsetMinutes, setEndOffsetMinutes] = useState(60);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (holidayRule) {
      const currentYear = moment().year().toString();
      const currentYearHolidayDate = holidayRule.holiday?.dates?.find((d) =>
        d.startsWith(currentYear),
      );
      const baseDate =
        currentYearHolidayDate ??
        holidayRule.date ??
        moment().format("YYYY-MM-DD");

      setName(holidayRule.name);
      setDate(baseDate);
      setNotes(holidayRule.notes || "");
      setStartOffsetMinutes(holidayRule.startOffsetMinutes || 0);
      setEndOffsetMinutes(holidayRule.endOffsetMinutes || 60);
      setAssignmentMode(holidayRule.assignmentMode || "every");
      setEveryYearParticipant(holidayRule.everyYearParticipant || undefined);
      setEvenYearsParticipant(holidayRule.evenYearsParticipant || undefined);
      setOddYearsParticipant(holidayRule.oddYearsParticipant || undefined);
    } else {
      setName("");
      setDate(moment().format("YYYY-MM-DD"));
      setNotes("");
      setStartOffsetMinutes(0);
      setEndOffsetMinutes(60);
      setAssignmentMode("every");
      setEveryYearParticipant(undefined);
      setEvenYearsParticipant(undefined);
      setOddYearsParticipant(undefined);
    }
  }, [isOpen, holidayRule]);

  const validateForm = () => {
    const newErrors: { name?: string } = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    if (startOffsetMinutes > endOffsetMinutes)
      newErrors.name = "Start must be before End.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const data = {
        name,
        notes,
        startOffsetMinutes,
        endOffsetMinutes,
        everyYearParticipant:
          assignmentMode === "every" ? everyYearParticipant : undefined,
        evenYearsParticipant:
          assignmentMode === "alternate" ? evenYearsParticipant : undefined,
        oddYearsParticipant:
          assignmentMode === "alternate" ? oddYearsParticipant : undefined,
        assignmentMode,
      };

      if (holidayRule) {
        await updateHolidayRule(holidayScheduleId, holidayRule._id, {
          ...data,
          holiday: holidayRule.holiday || undefined,
          date: date || holidayRule.date,
        });
      } else {
        await createHolidayRule(holidayScheduleId, {
          ...data,
          date,
        });
      }

      setIsOpen(false);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, name: err.message }));
    } finally {
      setIsSaving(false);
    }
  };

  const parsedDate = moment(date);
  const startDateTime = parsedDate.clone().add(startOffsetMinutes, "minutes");
  const endDateTime = parsedDate.clone().add(endOffsetMinutes, "minutes");

  const isCurrentYearOdd = moment().year() % 2 !== 0;

  return (
    <Dialog open={isOpen} onClose={setIsOpen}>
      <DialogTitle>
        {holidayRule ? "Edit Holiday Rule" : "Create Holiday Rule"}
      </DialogTitle>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Name</Label>
              <Input
                value={name}
                placeholder="Name"
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Field>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Start Date</Label>
                <Input
                  type="datetime-local"
                  value={startDateTime.format("YYYY-MM-DDTHH:mm")}
                  onChange={(e) => {
                    const newStart = moment(e.target.value);
                    const newOffset = newStart.diff(parsedDate, "minutes");
                    if (newOffset <= endOffsetMinutes) {
                      setStartOffsetMinutes(newOffset);
                    }
                  }}
                />
              </Field>
              <Field>
                <Label>End Date</Label>
                <Input
                  type="datetime-local"
                  value={endDateTime.format("YYYY-MM-DDTHH:mm")}
                  onChange={(e) => {
                    const newEnd = moment(e.target.value);
                    const newOffset = newEnd.diff(parsedDate, "minutes");
                    if (newOffset >= startOffsetMinutes) {
                      setEndOffsetMinutes(newOffset);
                    }
                  }}
                />
              </Field>
            </div>

            <Field>
              <Label>Assignment Mode</Label>
              <Select
                value={assignmentMode}
                onChange={(e) =>
                  setAssignmentMode(e.target.value as HolidayAssignmentMode)
                }
              >
                {Object.entries(holidayAssignmentModes).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </Select>
            </Field>

            {assignmentMode === "every" ? (
              <Field>
                <Label>Participant</Label>
                <Select
                  value={everyYearParticipant || ""}
                  onChange={(e) => setEveryYearParticipant(e.target.value)}
                >
                  <option value="">Select</option>
                  {participants.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <div className="flex gap-2">
                <Field className="w-1/2">
                  <Label>{`Even Years${isCurrentYearOdd ? "" : " (Current)"}`}</Label>
                  <Select
                    value={evenYearsParticipant || ""}
                    onChange={(e) => setEvenYearsParticipant(e.target.value)}
                  >
                    <option value="">Select</option>
                    {participants.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field className="w-1/2">
                  <Label>{`Odd Years${!isCurrentYearOdd ? "" : " (Current)"}`}</Label>
                  <Select
                    value={oddYearsParticipant || ""}
                    onChange={(e) => setOddYearsParticipant(e.target.value)}
                  >
                    <option value="">Select</option>
                    {participants.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            )}

            <Field>
              <Label>Notes</Label>
              <Textarea
                value={notes}
                placeholder="Notes"
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>

      <DialogActions>
        <Button plain onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <Button disabled={isSaving} onClick={handleSave}>
          {holidayRule ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidayRuleDialog;
